import { type FC } from "react";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import type { SectionInfoFormValues } from "./types";

import { LMSConnectionIndicator } from "components/materials/section/LMSConnectionIndicator";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { showDisconnectConfirmationModal } from "components/modals/DisconnectSectionModal";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextArea } from "mds/components/TextArea";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectDesignatedSection, selectLmsType } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";

type SectionInfoFormProps = {
  className?: string;
  form: UseFormReturn<SectionInfoFormValues>;
  onClose: () => void;
};

export const SectionInfoForm: FC<SectionInfoFormProps> = ({ className, form, onClose }) => {
  const designatedSection = useObjectSelector(selectDesignatedSection, [
    "id",
    "archived_at",
    "external_section_id",
    "kind",
    "lms_section_id",
    "template_id",
    "term_id",
  ]);
  const currentLmsType = useAppSelector(selectLmsType);
  const { canAuthorCourse } = useCapabilities();
  const { isArchived } = useIsArchived();

  const courseId = form.watch("course_id");
  const termId = form.watch("term_id");

  if (!designatedSection) {
    return null;
  }

  const onHandleSectionDisconnect = async () => {
    if (!designatedSection.lms_section_id || !currentLmsType) return;
    onClose();

    await showDisconnectConfirmationModal({
      sectionId: designatedSection.id,
    });
  };

  const readOnly = isLinkedSection(designatedSection) || isArchived(designatedSection);

  return (
    <FormProvider {...form}>
      <div className={twMerge("flex flex-col gap-3", className)}>
        {!readOnly && (
          <FormField label={t("home.courses.select_course_label")} name="course_id" required>
            {(controlProps) => (
              <CourseMenu
                {...controlProps}
                selectedCourseId={courseId}
                showEmptyOption={false}
                adminAccessibleOnly
                onCourseChange={(id) => form.setValue("course_id", id, { shouldDirty: true })}
              />
            )}
          </FormField>
        )}

        <FormField label={t("glossary.section_title")} name="title" readOnly={readOnly} required>
          {(controlProps) => (
            <TextField
              {...form.register("title", { required: t("error.field_required") })}
              {...controlProps}
            />
          )}
        </FormField>

        {!readOnly && (
          <FormField label={t("home.terms.select_term_label")} name="term_id" required>
            {(controlProps) => (
              <TermMenu
                {...controlProps}
                selectedTermId={termId}
                showEmptyOption={false}
                onTermChange={(id) => form.setValue("term_id", id, { shouldDirty: true })}
              />
            )}
          </FormField>
        )}

        <FormField label={t("glossary.section_description")} name="description" readOnly={readOnly}>
          {(controlProps) => (
            <TextArea
              {...form.register("description")}
              {...controlProps}
              minRows={3}
              placeholder={t("home.placeholder.section_description")}
            />
          )}
        </FormField>

        <FormField
          className={
            !canAuthorCourse && !designatedSection.external_section_id ? "hidden" : undefined
          }
          disabled={!canAuthorCourse}
          helper={t("settings.editable_admins_only")}
          label={t("settings.external_section_id")}
          name="external_section_id"
          readOnly={isArchived(designatedSection)}
        >
          {(controlProps) => (
            <TextField {...form.register("external_section_id")} {...controlProps} />
          )}
        </FormField>

        <div className="flex flex-col gap-1">
          <h3 className="mt-1 text-xs font-normal leading-[1.25rem] text-black-tint-20">
            {t("settings.lms_integration")}
          </h3>

          <div className="flex flex-row items-center justify-between">
            <LMSConnectionIndicator />

            {designatedSection.lms_section_id && (
              <SquareButton
                disabled={readOnly}
                kind="caution"
                size="xs"
                onClick={onHandleSectionDisconnect}
              >
                {t("settings.disconnect_section")}
              </SquareButton>
            )}
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
